import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceQuoteModal } from "@/components/ServiceQuoteModal";
import { FileCheck, Shield, Globe, Clock, CheckCircle, FileText, Scale, AlertTriangle } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useState } from "react";
import customsImg from "@/assets/customs.jpg";
import bannerTerminal from "@/assets/banner-terminal.jpg";

const CustomsBrokerage = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const services = [
    {
      icon: FileText,
      title: "Import/Export Clearance",
      description: "Complete customs clearance for all types of goods entering or leaving Greece and the EU",
      features: ["Electronic filing", "Document preparation", "Duty calculation", "Release coordination"]
    },
    {
      icon: Scale,
      title: "Tariff Classification",
      description: "Expert HS code classification to ensure compliance and optimize duty payments",
      features: ["HS code determination", "Binding rulings", "Classification appeals", "Tariff optimization"]
    },
    {
      icon: Shield,
      title: "Compliance & Audit",
      description: "Ensure your trade operations meet all regulatory requirements",
      features: ["Compliance reviews", "Internal audits", "Penalty mitigation", "Record keeping"]
    },
    {
      icon: Globe,
      title: "Trade Consulting",
      description: "Strategic advice on international trade regulations and opportunities",
      features: ["FTA utilization", "Origin determination", "Valuation advice", "Trade agreements"]
    }
  ];

  const specializations = [
    { title: "Temporary Imports", desc: "ATA carnets, temporary admission, and re-export procedures" },
    { title: "Bonded Warehousing", desc: "Duty suspension and deferred payment options" },
    { title: "Inward Processing", desc: "Manufacturing under customs suspension" },
    { title: "Transit Procedures", desc: "T1/T2 documents and common transit conventions" },
    { title: "Dangerous Goods", desc: "Specialized clearance for hazardous materials" },
    { title: "Food & Pharma", desc: "Health certificates and regulatory compliance" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src={customsImg} 
          alt="Customs Brokerage" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70" />
        <div className="relative z-10 text-center text-primary-foreground px-4 animate-fade-in">
          <FileCheck className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Customs Brokerage</h1>
          <p className="text-xl max-w-3xl mx-auto mb-8 opacity-90">
            Expert customs clearance and trade compliance services. Navigate complex regulations with confidence.
          </p>
          <Button size="lg" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
            Get Customs Quote
          </Button>
        </div>
      </section>

      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Overview */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Simplifying Global Trade</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Customs regulations can be complex and ever-changing. Our licensed customs brokers have decades of experience clearing goods through Greek and EU customs, ensuring your shipments move smoothly across borders.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  From routine clearances to complex trade scenarios, we provide the expertise you need to minimize delays, reduce costs, and maintain compliance.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">50,000+</div>
                    <div className="text-sm text-muted-foreground">Annual Clearances</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">99.8%</div>
                    <div className="text-sm text-muted-foreground">First-Time Clearance</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">4hr</div>
                    <div className="text-sm text-muted-foreground">Average Clearance Time</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">AEO</div>
                    <div className="text-sm text-muted-foreground">Certified Status</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={bannerTerminal} 
                  alt="Customs Operations" 
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </section>

          {/* Services */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Customs Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-muted-foreground mb-6">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 text-primary mr-2" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Process */}
          <section className="bg-secondary rounded-xl p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Customs Clearance Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {[
                { step: "1", title: "Document Receipt", desc: "Receive commercial documents" },
                { step: "2", title: "Classification", desc: "HS code & value verification" },
                { step: "3", title: "Filing", desc: "Electronic customs submission" },
                { step: "4", title: "Inspection", desc: "Coordinate if required" },
                { step: "5", title: "Release", desc: "Goods cleared for delivery" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Specializations */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Specialized Customs Procedures</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {specializations.map((item, index) => (
                <Card key={index} className="hover:shadow-lg transition-all hover-scale animate-fade-in">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Required Documents */}
          <section className="bg-secondary rounded-xl p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-8">Common Documents Required</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Commercial Invoice",
                "Packing List",
                "Bill of Lading / Airway Bill",
                "Certificate of Origin",
                "Insurance Certificate",
                "Import/Export License",
                "Health/Phytosanitary Certificates",
                "Technical Documentation"
              ].map((doc, index) => (
                <div key={index} className="flex items-center p-3 bg-background rounded-lg">
                  <FileText className="h-5 w-5 text-primary mr-3" />
                  <span className="text-sm">{doc}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Compliance Alert */}
          <section className="mb-20">
            <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-8 w-8 text-amber-500 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Stay Compliant</h3>
                    <p className="text-muted-foreground mb-4">
                      Non-compliance with customs regulations can result in significant penalties, delays, and seizure of goods. Our team stays current with all regulatory changes to protect your business.
                    </p>
                    <Button variant="outline" asChild>
                      <NavLink to="/contact">Request Compliance Review</NavLink>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Why Choose Us */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Why Choose Our Customs Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: Clock, title: "Speed", desc: "Fast clearance times with electronic filing and AEO status priority" },
                { icon: Shield, title: "Compliance", desc: "100% regulatory compliance with expert classification and valuation" },
                { icon: Globe, title: "Coverage", desc: "Clearance services at all Greek ports and airports, plus EU-wide support" }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="text-center hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-primary text-primary-foreground rounded-xl p-12 animate-fade-in">
            <h2 className="text-3xl font-bold mb-4">Need Customs Assistance?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Our customs experts are ready to help with your import/export requirements. Get in touch for a consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Request Customs Quote
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <NavLink to="/contact">Contact Customs Team</NavLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ServiceQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} serviceType="customs" />
    </div>
  );
};

export default CustomsBrokerage;
